import { ChipLinks, FinalCta, LinkList, Reveal, SectionTitle, SeoSection } from '@/components/seo/seo-blocks';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { COMPARISON_PAGES } from '@/content/seo/comparisons';
import {
  GUIDE_MOMENTS,
  GUIDE_PAGES,
  GUIDES_HUB_DESCRIPTION,
  GUIDES_HUB_TITLE,
  guidesByMoment,
} from '@/content/seo/guides';
import { SEGMENT_PAGES } from '@/content/seo/segments';
import { pageTitle } from '@/lib/seo/meta';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

/**
 * Hub dos guias (`/guias`): cada guia com a pergunta e a resposta de 1 linha,
 * agrupados pelo momento em que a pessoa procura; depois as comparações
 * (`#comparacoes`, destino do "Comparar" no caminho) e os ramos.
 */
export default function GuidesHubScreen() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: GUIDES_HUB_TITLE,
      description: GUIDES_HUB_DESCRIPTION,
      url: `${SITE_URL}/guias`,
      inLanguage: 'pt-BR',
      publisher: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Controlapag' },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          ...GUIDE_PAGES.map((guide) => ({ name: guide.question, url: `${SITE_URL}/guias/${guide.slug}` })),
          ...COMPARISON_PAGES.map((item) => ({ name: item.h1, url: `${SITE_URL}/comparar/${item.slug}` })),
        ].map((item, index) => ({ '@type': 'ListItem', position: index + 1, ...item })),
      },
    },
  ];

  return (
    <SeoPageShell
      screenTitle="Guias"
      dockLabel="Clientes, agenda e cobranças num lugar só"
      head={<SeoHead title={pageTitle(GUIDES_HUB_TITLE)} description={GUIDES_HUB_DESCRIPTION} path="/guias" jsonLd={jsonLd} />}
    >
      <SeoHero
        crumbs={[{ label: 'Início', href: '/' }, { label: 'Guias' }]}
        title="Guias para quem atende com hora marcada"
        sub="Falta, cancelamento, cobrança e preço. A resposta logo no começo."
      />

      <SeoSection narrow>
        {GUIDE_MOMENTS.map((moment, index) => {
          const guides = guidesByMoment(moment.id);
          if (!guides.length) return null;
          return (
            <View key={moment.id} className={index > 0 ? 'mt-12' : ''}>
              <ThemedText role="heading" aria-level={2} className="mb-4 font-bold text-2xl tracking-tight">
                {moment.label}
              </ThemedText>
              <View className="gap-3">
                {guides.map((guide) => (
                  <Reveal key={guide.slug}>
                    <Link href={`/guias/${guide.slug}` as Href} asChild>
                      <Pressable
                        accessibilityRole="link"
                        className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4 transition-colors hover:border-primary-200 md:p-5"
                      >
                        <View className="flex-1 gap-1">
                          <ThemedText role="heading" aria-level={3} className="font-semibold text-base">
                            {guide.question}
                          </ThemedText>
                          <ThemedText className="text-sm text-neutral-600">{guide.oneLine}</ThemedText>
                        </View>
                        <Ionicons name="arrow-forward" size={18} color="#FD5001" />
                      </Pressable>
                    </Link>
                  </Reveal>
                ))}
              </View>
            </View>
          );
        })}

        <View nativeID="comparacoes" className="mt-16">
          <SectionTitle title="Comparações" />
          <LinkList
            items={COMPARISON_PAGES.map((item) => ({ label: item.h1, href: `/comparar/${item.slug}` as Href }))}
          />
        </View>

        <View className="mt-16">
          <SectionTitle title="Controlapag para o seu ramo" />
          <ChipLinks
            items={SEGMENT_PAGES.map((segment) => ({ label: segment.label, href: `/para/${segment.slug}` as Href }))}
          />
        </View>
      </SeoSection>

      <FinalCta title="Organize clientes, agenda e cobranças num lugar só" />
    </SeoPageShell>
  );
}
